import React from 'react';

import { Stack } from '../../components/stack';
import {
  type DashboardAppBarContentProps,
  DashboardAppBar,
  DashboardAppBarContent,
} from '../dashboard-app-bar';
import { PrintHeader, type PrintHeaderProps } from '../print-header';

import { Navigation, type NavigationProps } from './navigation/navigation';
import { NavigationProvider } from './navigation/navigation-provider';
import { Root } from './root/root';

export type DashboardLayoutProps = React.PropsWithChildren<{
  applicationName: string;
  navigation: Omit<NavigationProps, 'applicationName' | 'isLoading'> & {
    routePrefix?: string;
  };
  isNavigationLoading?: boolean;
  appBar: Omit<DashboardAppBarContentProps, 'routePrefix'>;
  printHeader?: Omit<PrintHeaderProps, 'applicationName'>;
}>;

/** @experimental In active development. Avoid using until further notice. */
export const DashboardLayout = ({
  applicationName,
  children,
  navigation,
  isNavigationLoading,
  appBar,
  printHeader,
}: DashboardLayoutProps) => {
  const { routePrefix = '/', ...navigationRest } = navigation;

  return (
    <>
      <PrintHeader applicationName={applicationName} {...printHeader} />
      <Root>
        <NavigationProvider routePrefix={routePrefix}>
          <Navigation
            applicationName={applicationName}
            isLoading={isNavigationLoading}
            {...navigationRest}
          />
        </NavigationProvider>
        <Stack sx={{ flex: 1, overflow: 'hidden' }}>
          <DashboardAppBar>
            <DashboardAppBarContent routePrefix={routePrefix} {...appBar} />
          </DashboardAppBar>
          <Stack
            sx={{
              flex: 1,
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            {children}
          </Stack>
        </Stack>
      </Root>
    </>
  );
};
